import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { BoschUiButtonTypeEnum } from '../../atoms/bosch-ui-button/bosch-ui-button.model';
import { BoschUiButtonComponent } from '../../atoms/bosch-ui-button/bosch-ui-button.component';
import { ComponentType } from '@angular/cdk/portal';
import { BOSCH_UI_DIALOG_DATA } from '../../basics/ui-dialog-data.provider';
import { BoschUiDialogConfigType } from './bosch-ui-dialog.model';
import { AsyncPipe } from '@angular/common';
import { BoschUiDialog } from './bosch-ui-dialog.service';
import { combineLatest, debounceTime, map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bosch-ui-dialog',
  templateUrl: './bosch-ui-dialog.component.html',
  styleUrls: ['./bosch-ui-dialog.component.scss'],
  imports: [BoschUiButtonComponent, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
// TODO: solve next problems
// - return dialogRef from .open() method
// - implement dialogRef afterClosed functionality
// - add multiple popups functionality
// - try to rewrite this component using cdk overlay
export class BoschUiDialogComponent implements OnInit, OnDestroy {
  public readonly dialogService = inject(BoschUiDialog);
  private readonly viewContainerRef = inject(ViewContainerRef);
  @Input() component: unknown | null = null;
  @Input() set config(config: BoschUiDialogConfigType) {
    if (config) {
      this._config = { ...this._config, ...config };
    }
  }
  get config(): BoschUiDialogConfigType {
    return this._config;
  }
  @HostBinding('style.z-index') get zIndex(): number {
    return this.dialogService.index;
  }
  @ViewChild('dialog', { static: true })
  dialog: ElementRef | null = null;
  @ViewChild('dialogTitle', { read: ViewContainerRef, static: false }) title: ViewContainerRef | null = null;
  @ViewChild('dialogHeadline', { read: ViewContainerRef, static: false }) headline: ViewContainerRef | null = null;
  @ViewChild('dialogBody', { read: ViewContainerRef, static: true }) body: ViewContainerRef | null = null;
  @ViewChild('dialogActions', { read: ViewContainerRef, static: false }) actions: ViewContainerRef | null = null;

  private _config: BoschUiDialogConfigType = {
    data: null,
    hasCloseButton: true,
    type: null,
  };
  private _unsubscribe$ = new Subject<void>();

  buttonType = BoschUiButtonTypeEnum;
  closeButtonWithoutHeader$ = this.dialogService.hasTitle$.pipe(
    map((hasTitle: boolean) => !hasTitle && this.config.hasCloseButton),
  );

  ngOnInit(): void {
    if (this.component && this.body && this.dialog) {
      const injector = Injector.create({
        providers: [{ provide: BOSCH_UI_DIALOG_DATA, useValue: this._config.data }],
      });

      const componentRef = this.viewContainerRef.createComponent(this.component as ComponentType<unknown>, {
        injector,
      });

      componentRef.changeDetectorRef.detectChanges();
      this.iniShowDialogSubscription();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
  }

  close(): void {
    if (this.dialog) {
      (this.dialog.nativeElement as HTMLDialogElement).close();
    }

    this.dialogService.close();
  }

  private iniShowDialogSubscription(): void {
    combineLatest([this.dialogService.hasTitle$, this.dialogService.hasHeadline$, this.dialogService.hasActions$])
      .pipe(takeUntil(this._unsubscribe$), debounceTime(0))
      .subscribe(() => {
        if (this.dialog) {
          // N.B.: this code opens dialog element using Fullscreen API
          (this.dialog.nativeElement as HTMLDialogElement).showModal();
        }
      });
  }
}
